import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["step", "nextButton", "prevButton", "progressStep", "progressText"]
  static values = { currentStep: Number, totalSteps: Number }

  connect() {
    this.currentStepValue = 1
    this.totalStepsValue = 1 // Temporarily set to 1 since we only have one step
    this.updateProgress()
  }

  nextStep() {
    if (this.validateCurrentStep()) {
      // For now, just submit the form since we only have one step
      console.log('Submitting form...')
      const form = this.element.querySelector('form')
      console.log('Form action:', form.action)
      console.log('Form method:', form.method)
      form.submit()
    }
  }

  prevStep() {
    this.currentStepValue = Math.max(this.currentStepValue - 1, 1)
    this.showCurrentStep()
    this.updateProgress()
  }

  showCurrentStep() {
    // Hide all steps
    this.stepTargets.forEach((step, index) => {
      if (index + 1 === this.currentStepValue) {
        step.classList.remove('hidden')
      } else {
        step.classList.add('hidden')
      }
    })

    // Update navigation buttons
    this.updateNavigationButtons()
  }

  updateProgress() {
    // Update progress steps
    this.progressStepTargets.forEach((step, index) => {
      const stepNumber = index + 1
      if (stepNumber < this.currentStepValue) {
        // Completed step
        step.classList.remove('border-gray-300', 'bg-white')
        step.classList.add('bg-indigo-600')
        step.innerHTML = '<svg class="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>'
      } else if (stepNumber === this.currentStepValue) {
        // Current step
        step.classList.remove('border-gray-300', 'bg-white', 'bg-indigo-600')
        step.classList.add('border-indigo-600', 'bg-white')
        step.innerHTML = `<span class="text-sm font-medium text-indigo-600">${stepNumber}</span>`
      } else {
        // Future step
        step.classList.remove('border-indigo-600', 'bg-indigo-600')
        step.classList.add('border-gray-300', 'bg-white')
        step.innerHTML = `<span class="text-sm font-medium text-gray-500">${stepNumber}</span>`
      }
    })

    // Update progress text
    if (this.hasProgressTextTarget) {
      const stepNames = ['Basic Information', 'Psychographic Profile', 'Behavioral Patterns', 'Type-Specific Details']
      this.progressTextTarget.textContent = `Step ${this.currentStepValue} of ${this.totalStepsValue}: ${stepNames[this.currentStepValue - 1]}`
    }
  }

  updateNavigationButtons() {
    if (this.hasPrevButtonTarget) {
      if (this.currentStepValue === 1) {
        this.prevButtonTarget.classList.add('invisible')
      } else {
        this.prevButtonTarget.classList.remove('invisible')
      }
    }

    if (this.hasNextButtonTarget) {
      if (this.currentStepValue === this.totalStepsValue) {
        this.nextButtonTarget.textContent = 'Create Persona'
        this.nextButtonTarget.classList.remove('bg-indigo-600', 'hover:bg-indigo-700')
        this.nextButtonTarget.classList.add('bg-green-600', 'hover:bg-green-700')
      } else {
        this.nextButtonTarget.textContent = 'Next Step'
        this.nextButtonTarget.classList.remove('bg-green-600', 'hover:bg-green-700')
        this.nextButtonTarget.classList.add('bg-indigo-600', 'hover:bg-indigo-700')
      }
    }
  }

  validateCurrentStep() {
    const currentStepElement = this.stepTargets[this.currentStepValue - 1]
    const requiredFields = currentStepElement.querySelectorAll('[required]')
    let isValid = true

    // Check text and select fields
    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        field.classList.add('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
        isValid = false
      } else {
        field.classList.remove('border-red-500', 'focus:border-red-500', 'focus:ring-red-500')
      }
    })

    // Check radio button groups
    const personaTypeSelected = currentStepElement.querySelector('input[name="persona[persona_type_id]"]:checked')
    const genderPreferenceSelected = currentStepElement.querySelector('input[name="persona[gender_preference_id]"]:checked')

    if (!personaTypeSelected) {
      console.log('Persona type not selected')
      isValid = false
    }

    if (!genderPreferenceSelected) {
      console.log('Gender preference not selected')
      isValid = false
    }

    if (!isValid) {
      this.showValidationError()
    }

    return isValid
  }

  showValidationError() {
    // Create or update validation error message
    let errorDiv = this.element.querySelector('.validation-error')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.className = 'validation-error rounded-md bg-red-50 p-4 mb-6'
      errorDiv.innerHTML = `
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Please fill in all required fields</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>Please complete all required fields before proceeding to the next step.</p>
            </div>
          </div>
        </div>
      `
      const currentStepElement = this.stepTargets[this.currentStepValue - 1]
      currentStepElement.insertBefore(errorDiv, currentStepElement.firstChild)
    }
  }

  updatePersonaType(event) {
    const personaType = event.target.value
    const currentStepElement = this.stepTargets[this.currentStepValue - 1]
    
    // Show/hide type-specific fields based on selection
    const typeSpecificFields = currentStepElement.querySelectorAll('[data-persona-type]')
    typeSpecificFields.forEach(field => {
      if (field.dataset.personaType === personaType) {
        field.classList.remove('hidden')
      } else {
        field.classList.add('hidden')
      }
    })
  }

  // Handle form submission on final step
  submitForm() {
    if (this.currentStepValue === this.totalStepsValue) {
      this.element.querySelector('form').submit()
    } else {
      this.nextStep()
    }
  }
} 